/**
 * @module LegendSetting/useSeriesSortDraft
 * @description 图例系列排序功能簇 —— sort_series / series_order / series_sorts
 *
 * 读写与副作用收敛在本 Hook；协议拼装见 `seriesSortByUtils.ts`。
 * UI 仅消费 SortByPanel viewModel，禁止裸 dispatch。
 *
 * 与 X 轴排序（useXAxisSortDraft）结构对称，差异在于上传字段与种子来源（系列名列表）。
 */

import { useCallback, useMemo, useRef, useEffect } from 'react';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import {
  readStructuredArrayRespectingDraftKey,
  useChartSettingsSelector,
  usePatchDraft,
  useSettingState,
} from '../../context';
import { useQueryColumnMetrics } from '../../hooks/deps';
import { useSeriesQuickDragList } from '../../hooks/useSeriesQuickDragList';
import { SortByEnum, type TSortByEnum } from '../common/SortByPanel/SortType';
import type { ICustomMetricSortValueItem } from '../common/SortByPanel/CustomMetricSort';
import type { IQuickDragItem } from '@dbl/components/QuickDragList/types';
import type { ISortByPanelViewModel } from '../common/SortByPanel/types';
import type { ISeriesSortValueItem } from '../../types/settings/sortFields';
import {
  buildManualSeriesSortPayload,
  buildMetricSeriesSortPayload,
  buildSortTypeChangePayload,
} from './seriesSortByUtils';

export function useSeriesSortDraft(): ISortByPanelViewModel {
  const { formatMessage, locale } = useIntl();
  const patchDraft = usePatchDraft();

  // ===== 1) 外部数据：QB 指标列表 + 图表当前系列名（手动排序种子） =====
  const columnMetrics = useQueryColumnMetrics();
  /** 柱状/堆叠等图的 series 顺序，用于手动模式初次落库与空 series_order 展示 */
  const quickDragSeed = useSeriesQuickDragList();

  // ===== 2) 配置层：sort_series（Schema）+ 系列序 / 指标序（Schema 数组） =====
  const { value: sortSeries } = useSettingState('sort_series');

  /**
   * 手动系列序：readStructuredArrayRespectingDraftKey，不用 useSettingObjectState。
   * draft 显式 [] 时须保留空序，不拾取 base 旧 series_order（见 context/useSettingState 注释）。
   */
  const seriesOrder = useChartSettingsSelector(s =>
    readStructuredArrayRespectingDraftKey(s.draft, s.base, 'series_order'),
  );

  /** 按指标排序：同上，避免 draft 非法数组时回退 base。 */
  const seriesSorts = useChartSettingsSelector(s =>
    readStructuredArrayRespectingDraftKey(s.draft, s.base, 'series_sorts'),
  );

  // ===== 3) ref 快照：切换排序方式时 buildSortTypeChangePayload 需最新兄弟字段 =====
  const seriesSortsRef = useRef(seriesSorts);
  seriesSortsRef.current = seriesSorts;
  const seriesOrderRef = useRef(seriesOrder);
  seriesOrderRef.current = seriesOrder;
  const quickDragSeedRef = useRef(quickDragSeed);
  quickDragSeedRef.current = quickDragSeed;
  const columnMetricsRef = useRef(columnMetrics);
  columnMetricsRef.current = columnMetrics;

  const getSortState = useCallback(
    () => ({
      seriesSorts: seriesSortsRef.current,
      seriesOrder: seriesOrderRef.current,
      quickDragSeed: quickDragSeedRef.current,
      columnMetrics: (columnMetricsRef.current || []) as unknown[],
    }),
    [],
  );

  const seedStrings = useMemo(() => [...quickDragSeed], [quickDragSeed]);

  const lastSyncedSeedKeyRef = useRef('');

  /**
   * 手动系列排序 + 图表系列变化：把当前系列名写入 series_order。
   *
   * 业务原因：用户选「手动」时若尚未保存顺序，需用图表实时系列列表初始化拖拽区；
   * 种子内容未变时不重复 patch，避免与图表刷新形成更新环。
   */
  useEffect(() => {
    if (sortSeries !== SortByEnum.Manual) {
      lastSyncedSeedKeyRef.current = '';
      return;
    }
    if (!seedStrings.length) {
      return;
    }

    const seedKey = seedStrings.join('\0');
    if (lastSyncedSeedKeyRef.current === seedKey) {
      return;
    }
    lastSyncedSeedKeyRef.current = seedKey;

    patchDraft(buildManualSeriesSortPayload(seedStrings, quickDragSeed));
  }, [sortSeries, seedStrings, quickDragSeed, patchDraft]);

  // ===== 4) 用户交互 =====

  /**
   * 切换默认 / 按指标 / 手动：一次性写入 sort_series 并清理互斥字段
   *（见 seriesSortByUtils.buildSortTypeChangePayload）。
   */
  const handleSortTypeChange = useCallback(
    (value: string) => {
      patchDraft(
        buildSortTypeChangePayload(value as TSortByEnum, getSortState()),
      );
    },
    [patchDraft, getSortState],
  );

  /**
   * 手动拖拽结束：更新 series_order，sort_series 保持 manual；
   * 仍传入 quickDragSeed 以便 payload 与图表系列元数据对齐。
   */
  const handleDragChange = useCallback(
    (items: IQuickDragItem[]) => {
      const ordered = items.map(item => String(item.id ?? item.text ?? ''));
      patchDraft(
        buildManualSeriesSortPayload(ordered, quickDragSeedRef.current),
      );
    },
    [patchDraft],
  );

  /** 按指标排序：写入 series_sorts，并置 sort_series 为 metric */
  const handleMetricSortChange = useCallback(
    (newValue: ICustomMetricSortValueItem[]) => {
      patchDraft(
        buildMetricSeriesSortPayload(newValue as ISeriesSortValueItem[]),
      );
    },
    [patchDraft],
  );

  const customOptions = useMemo(
    () =>
      columnMetrics.map(m => {
        const columnName = String(m.column_name ?? '');
        const isEn = locale === LANGUAGE_EN;
        const displayLabel = isEn
          ? m.column_display_en || m.column_display || columnName
          : m.column_display || columnName;
        return {
          ...m,
          value: columnName,
          table_code: String(m.table_code || 't0'),
          column_display: displayLabel,
        };
      }),
    [columnMetrics, locale],
  );

  /** 拖拽列表：已保存 series_order 优先，否则展示图表系列种子 */
  const dragList = useMemo<IQuickDragItem[]>(() => {
    const names = seriesOrder.length > 0 ? seriesOrder : seedStrings;
    return names.map(name => ({ id: name, text: name }));
  }, [seriesOrder, seedStrings]);

  // ===== 5) SortByPanel 契约（图例区专用 header 样式） =====
  return {
    sortMode: sortSeries,
    /** 字段排序仅用于轴排序，图例系列排序永久隐藏该选项 */
    hiddenOptions: [SortByEnum.DatasetFieldSort],
    showMetricSort: sortSeries === SortByEnum.Metric,
    showManualSort: sortSeries === SortByEnum.Manual,
    showDatasetFieldSort: false,
    metricSortValue: seriesSorts,
    metricSortOptions: customOptions,
    manualDragTitle: formatMessage({ id: 'dbl_chart_legend_setting' }),
    manualDragList: dragList,
    onSortTypeChange: handleSortTypeChange,
    onMetricSortChange: handleMetricSortChange,
    onManualDragChange: handleDragChange,
    headerClassName: 'dbl-legend-setting__sort-header',
  };
}
